import type { ChatMessage, SimpleExamplesConfig } from '@demo-portal/shared';
import React, { useEffect, useRef, useState } from 'react';
import MarkdownRenderer from './MarkdownRenderer';

interface ChatInterfaceProps {
  messages: ChatMessage[];
  isProcessing: boolean;
  examples: SimpleExamplesConfig;
  welcomeMessage:string;
  onSendMessage: (message: string) => void;
  primaryColor?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isProcessing,
  examples,
  welcomeMessage,
  onSendMessage,
  primaryColor = '#3498db',
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isProcessing) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleExampleClick = (query: string) => {
    if (!isProcessing) {
      onSendMessage(query);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '25px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        fontSize: '1.4em',
        fontWeight: 'bold',
        marginBottom: '15px',
        color: '#2c3e50',
        borderBottom: `2px solid ${primaryColor}`,
        paddingBottom: '8px',
        flexShrink: 0,
      }}>
        💬 Consultation Chat
      </div>

      <div style={{
        flex: 1,
        overflowY: 'auto',
        border: '1px solid #e1e8ed',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
        background: '#fafbfc',
        minHeight: 0, // Important for flex child to shrink
      }}>
        {/* Welcome message */}
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          borderRadius: '10px',
          background: '#f1f8e9',
          borderLeft: '5px solid #4caf50',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            AI System
          </div>
          <MarkdownRenderer content={welcomeMessage} />
        </div>

        {/* Chat messages */}
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              marginBottom: '20px',
              padding: '15px',
              borderRadius: '10px',
              background: message.role === 'user' ? '#e3f2fd' : '#f1f8e9',
              borderLeft: `5px solid ${message.role === 'user' ? '#2196f3' : '#4caf50'}`,
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {message.role === 'user' ? 'You' : 'AI System'}
            </div>
            {message.role === 'user' ? (
              <div style={{ whiteSpace: 'pre-line' }}>
                {message.content}
              </div>
            ) : (
              <MarkdownRenderer content={message.content} />
            )}
          </div>
        ))}

        {/* Processing indicator */}
        {isProcessing && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '15px',
            margin: '10px 0',
            textAlign: 'center',
            color: '#856404',
          }}>
            🤖 Processing your request through our expert agents...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input and examples section */}
      <div style={{ flexShrink: 0 }}>
        {/* Input form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '12px' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyUp={handleKeyPress}
            placeholder="Describe your requirements..."
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: '15px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              borderColor: inputRef.current === document.activeElement ? primaryColor : '#ddd',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={isProcessing || !inputValue.trim()}
            style={{
              padding: '15px 30px',
              background: isProcessing || !inputValue.trim() ? '#bbb' : '#2c3e50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isProcessing || !inputValue.trim() ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            {isProcessing ? 'Processing...' : 'Send'}
          </button>
        </form>

        {/* Example queries */}
        <div style={{ marginTop: '20px' }}>
          <strong>Example scenarios:</strong><br />
          <div style={{ marginTop: '10px' }}>
            {examples.slice(0, 3).map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleClick(example.query)}
                disabled={isProcessing}
                style={{
                  background: '#ecf0f1',
                  border: '1px solid #bdc3c7',
                  padding: '10px 15px',
                  margin: '5px',
                  borderRadius: '25px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isProcessing ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isProcessing) {
                    e.currentTarget.style.background = '#d5dbdb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProcessing) {
                    e.currentTarget.style.background = '#ecf0f1';
                  }
                }}
              >
                {example.category}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;